'use client';

import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import Link from 'next/link';
import { useLocale } from 'next-intl';

import {
  DispatchedUserMessageProvider,
  type DispatchedUserMessageProps,
} from '@/components/chat/thread-dispatch-message-slot';
import { TOOL_ROW_EXPAND_TRANSITION } from '@/components/chat/tool-calls/tool-activity-cluster';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { getYourImaiCopy } from '@/i18n/your-imai-copy';
import { api, useQueryWithStatus } from '@/lib/convex';
import { cn } from '@/lib/utils';
import { yourImaiHref } from '@/lib/your-imai-routes';

import { YourImaiAvatar } from './your-imai-avatar';
import { yourImaiDispatchView } from './your-imai-dispatch-view';

/** The badge row, shared by the pending hold. */
const BADGE_STACK_CLASS = 'flex w-full min-w-0 flex-col items-end gap-1';

/**
 * A message the member's own Your IMAI sent into a thread: instead of the
 * full handoff prompt, a compact "Message from Nova" badge in that IMAI's
 * bubble colour, with no preview line under it (Raj, 10-14). Clicking or
 * tapping it expands the message in place as the normal user bubble, with
 * a link to the Your IMAI chat; a pointer hover still peeks at it while
 * collapsed.
 * Any other dispatched message keeps the default.
 */
function YourImaiDispatchedMessage({
  orgSlug,
  messageId,
  prompt,
  bubble,
  fallback,
}: DispatchedUserMessageProps) {
  const copy = getYourImaiCopy(useLocale());
  const promptId = useId();
  const contentId = useId();
  const reduceMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(false);
  const [peeking, setPeeking] = useState(false);
  // Clip only while the height animates, so the bubble's shadow shows.
  const [settled, setSettled] = useState(false);
  const lookup = useQueryWithStatus(
    api.your_imai.children.getYourImaiDispatchSource,
    { orgSlug, messageId }
  );
  const view = yourImaiDispatchView({
    lookup,
    prompt,
    defaultName: copy.imaiName,
    labelTemplate: copy.messageFrom,
  });
  if (view.kind === 'default') return fallback;
  // The collapsed badge's height while the source resolves, so the long
  // prompt never flashes in before it collapses.
  if (view.kind === 'pending')
    return (
      <div aria-hidden className={BADGE_STACK_CLASS}>
        <div className="h-7" />
      </div>
    );
  const toggle = () => {
    setSettled(Boolean(reduceMotion));
    setExpanded((open) => !open);
  };
  return (
    <div className={BADGE_STACK_CLASS}>
      {/* Touch never hovers, and the peek would only repeat the open note. */}
      <HoverCard
        open={peeking && !expanded}
        onOpenChange={setPeeking}
        openDelay={200}
        closeDelay={120}
      >
        <HoverCardTrigger asChild>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-describedby={expanded ? undefined : promptId}
            onClick={toggle}
            style={{
              backgroundColor: view.colors.background,
              color: view.colors.foreground,
            }}
            // The `after` box grows the tap target to 40px without moving it.
            className="type-meta-strong focus-visible:ring-ring focus-visible:ring-offset-background relative inline-flex h-7 max-w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-full ps-1 pe-2 transition-[filter] outline-none after:absolute after:inset-x-0 after:-inset-y-1.5 after:content-[''] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <YourImaiAvatar
              persona={view.persona}
              size={20}
              className="shrink-0"
            />
            <span className="truncate">{view.label}</span>
            <ChevronDown
              aria-hidden
              className={cn(
                'size-3.5 shrink-0 opacity-80 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out',
                expanded && 'rotate-180'
              )}
            />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="end"
          side="bottom"
          className="w-96 max-w-[calc(100vw-2rem)] p-0"
        >
          <p className="type-body text-foreground max-h-80 overflow-y-auto overscroll-contain px-3.5 py-3 text-start [overflow-wrap:anywhere] whitespace-pre-wrap">
            {view.prompt}
          </p>
        </HoverCardContent>
      </HoverCard>
      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            key="note"
            id={contentId}
            initial={reduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { height: 0 }}
            transition={TOOL_ROW_EXPAND_TRANSITION}
            onAnimationComplete={() => setSettled(true)}
            style={{ overflow: settled ? 'visible' : 'hidden' }}
            className="flex w-full min-w-0 flex-col items-end"
          >
            {bubble}
            <Link
              href={yourImaiHref(orgSlug)}
              className="type-meta text-muted-foreground hover:text-foreground focus-visible:ring-ring me-1 mb-0.5 rounded-sm underline-offset-4 outline-none hover:underline focus-visible:ring-2"
            >
              {copy.goToChat}
            </Link>
          </motion.div>
        ) : null}
      </AnimatePresence>
      {/* The prompt for assistive tech while it is collapsed. */}
      <span id={promptId} hidden>
        {view.prompt}
      </span>
    </div>
  );
}

function renderYourImaiDispatchedMessage(props: DispatchedUserMessageProps) {
  return <YourImaiDispatchedMessage {...props} />;
}

/** Presents the member's own Your IMAI handoffs in every workspace thread. */
export function YourImaiDispatchedMessageProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <DispatchedUserMessageProvider render={renderYourImaiDispatchedMessage}>
      {children}
    </DispatchedUserMessageProvider>
  );
}
